// ============================================================
// ArmourBodySection - what kind of body, and the armour on it.
//
// SHAPE AND NUMBER ARE ASKED APART (v8.44.0): a horde of vehicles is
// sayable. The armour then follows the shape: a humanoid's six locations
// laid out as a body, a vehicle's three facings, an amorphous mass.
//
// Each location carries two numbers. The big one is the armour as it is
// NOW (live state: Corrosive and Sunder wear it down); the small one is
// the suit as issued (the sheet). Editing the suit moves every location
// nobody has damaged yet.
// ============================================================
import type { Character } from '../../engine/character/types';
import { BODY_TYPE_LABELS, getBodyConfig } from '../../engine/bodyConfig';
import { BODY_SHAPES, hasArmourLocations, isCrowd, resolveBodyShape, type BodyShape } from '../../engine/bodyShape';
import { bodySwitchWarning, isSameBody, withPath } from '../../engine/targetEdits';
import { effectiveVehicleFacing, VEHICLE_FACING_CHOICES, VEHICLE_FACING_LABELS } from '../../engine/vehicleFacing';
import { VEHICLE_KINDS, VEHICLE_KIND_LABELS, vehicleKindOf } from '../../engine/vehicleKind';
import type { Target } from '../../types';
import clsx from '../../utils/clsx';
import ObjectSection from './ObjectSection';
import ReadoutNumber from './ReadoutNumber';
import styles from './Sheet.module.css';
import type { SectionProps } from './VitalsSection';

interface Props extends SectionProps {
  /** Re-body the combatant: sheet and state in one write. */
  onRebody: (shape: BodyShape, crowd: boolean) => void;
}

/** The shape radios and the horde tick; a damaged or conditioned body asks before it resets anything. */
function BodyChoice({ target, sheetLocked, onRebody }: Props) {
  const shape = resolveBodyShape(target);
  const crowd = isCrowd(target);
  const apply = (nextShape: BodyShape, nextCrowd: boolean) => {
    // An object is one thing, never a crowd of them.
    if (nextShape === 'object') nextCrowd = false;
    if (isSameBody(target, nextShape, nextCrowd)) return;
    const warning = bodySwitchWarning(target);
    if (warning && !window.confirm(warning)) return;
    onRebody(nextShape, nextCrowd);
  };
  return (
    <div className={styles.choiceRow} role="group" aria-label="Body configuration">
      {BODY_SHAPES.map((option) => (
        <label key={option} className={clsx(styles.choice, shape === option && styles.choiceOn)}>
          <input
            type="radio"
            name="bodyShape"
            data-testid={`body-shape-${option}`}
            checked={shape === option}
            disabled={sheetLocked}
            onChange={() => apply(option, crowd)}
          />
          {BODY_TYPE_LABELS[option]}
        </label>
      ))}
      <label
        className={clsx(styles.choice, crowd && styles.choiceOn)}
        title="Many of them, fought as one: magnitude instead of wounds"
      >
        <input
          type="checkbox"
          data-testid="body-horde"
          checked={crowd}
          disabled={sheetLocked || shape === 'object'}
          onChange={(e) => apply(shape, e.target.checked)}
        />
        {BODY_TYPE_LABELS.horde}
      </label>
    </div>
  );
}

interface PlateProps extends SectionProps {
  id: string;
  name: string;
  /** In play right now (a vehicle's active facing). */
  active?: boolean;
}

/** One armoured location: the armour now, over the suit as issued. */
function Plate({
  id,
  name,
  active,
  character,
  target,
  onChange,
  onTargetChange,
  sheetLocked,
  stateLocked,
}: PlateProps) {
  const worn = target.armour[id] ?? 0;
  const issued = target.armourOriginal?.[id] ?? worn;
  // The field SHOWS the pinned suit when there is a pin, so setting it lets that pin go.
  const setIssued = (value: number) =>
    onChange(
      { armour: { ...character.armour, locations: { ...character.armour.locations, [id]: value } } },
      undefined,
      [{ key: 'armour', leaf: id }],
    );
  return (
    <div className={clsx(styles.plate, active && styles.plateActive, worn < issued && styles.plateWorn)} data-loc={id}>
      <ReadoutNumber
        label={name}
        size="large"
        min={0}
        value={worn}
        disabled={stateLocked}
        onChange={(value) => onTargetChange(withPath(target, `armour.${id}`, value))}
      />
      <span className={styles.plateIssued}>
        of
        <ReadoutNumber
          ariaLabel={`${name} as issued`}
          size="small"
          min={0}
          value={issued}
          disabled={sheetLocked}
          onChange={setIssued}
        />
      </span>
    </div>
  );
}

/**
 * What kind of vehicle - a tank, a walker, a skimmer, a bike (plan
 * map-display, B). How the display draws it, never a rule: the sheet's, so a
 * statblock's choice is pinned like any sheet edit.
 */
function KindChoice({ target, sheetLocked, onChange }: SectionProps) {
  // What the display draws: the projection, so a pinned kind shows as chosen.
  const chosen = vehicleKindOf(target);
  return (
    <div className={styles.choiceRow} role="group" aria-label="Vehicle kind">
      {VEHICLE_KINDS.map((kind) => (
        <label key={kind} className={clsx(styles.choice, chosen === kind && styles.choiceOn)}>
          <input
            type="radio"
            name="vehicleKind"
            data-testid={`vehicle-kind-${kind}`}
            checked={chosen === kind}
            disabled={sheetLocked}
            onChange={() => onChange({ vehicleKind: kind })}
          />
          {VEHICLE_KIND_LABELS[kind]}
        </label>
      ))}
    </div>
  );
}

/** The facing choice. Auto is first, and the default: the map knows where the shot came from (v8.52.0). */
function FacingChoice({ target, stateLocked, onTargetChange }: SectionProps) {
  const chosen = target.vehicleFacing ?? 'auto';
  return (
    <div className={styles.choiceRow} role="group" aria-label="Active facing">
      {VEHICLE_FACING_CHOICES.map((facing) => (
        <label
          key={facing}
          className={clsx(styles.choice, chosen === facing && styles.choiceOn)}
          title={
            facing === 'auto'
              ? 'Let the map work it out from where the shooter is standing - each attacker gets their own answer'
              : `Always present the ${facing}, whatever the map says`
          }
        >
          <input
            type="radio"
            name="vehicleFacing"
            data-testid={`vehicle-facing-${facing}`}
            checked={chosen === facing}
            disabled={stateLocked}
            onChange={() => onTargetChange({ ...target, vehicleFacing: facing } as Target)}
          />
          {VEHICLE_FACING_LABELS[facing]}
          {facing === 'auto' && chosen === 'auto' && target.vehicleFacingFromMap
            ? ` (${target.vehicleFacingFromMap})`
            : ''}
        </label>
      ))}
    </div>
  );
}

const FACINGS = ['front', 'side', 'rear'] as const;

/** The plates for the body's shape. */
function Plates(props: SectionProps) {
  const { target } = props;
  const shape = resolveBodyShape(target);
  if (shape === 'vehicle') {
    const inPlay = effectiveVehicleFacing(target);
    return (
      <>
        <FacingChoice {...props} />
        <div className={styles.plates} data-body="vehicle">
          {FACINGS.map((f) => (
            <Plate key={f} {...props} id={f} name={`${VEHICLE_FACING_LABELS[f]} AP`} active={inPlay === f} />
          ))}
        </div>
        <div className={styles.sectionNote}>All hits use the active facing. Location decides the crit table only.</div>
      </>
    );
  }
  if (shape === 'amorphous') {
    return (
      <div className={styles.plates} data-body="amorphous">
        <Plate {...props} id="mass" name="Mass AP" />
      </div>
    );
  }
  if (shape === 'object') {
    return (
      <div className={styles.plates} data-body="object">
        <Plate {...props} id="structure" name="Structure AP" />
      </div>
    );
  }
  const bodyType = target.bodyType || 'humanoid';
  return (
    <div className={styles.plates} data-body="humanoid">
      {getBodyConfig(bodyType).locations.map((loc) => (
        <Plate key={loc.id} {...props} id={loc.id} name={loc.name} />
      ))}
    </div>
  );
}

/**
 * What the suit is called: "Mk VII Aquila". Labelled "Suit", not "Armour
 * name": a label containing "name" is matched by every test and screen
 * reader search for the sheet's Name field.
 */
function ArmourName({ character, onChange, sheetLocked }: SectionProps) {
  return (
    <label className={styles.readout}>
      <span className={styles.readoutLabel}>Suit</span>
      <input
        className={styles.textInput}
        value={character.armour?.name ?? ''}
        placeholder="e.g. Mk VII Aquila"
        disabled={sheetLocked}
        onChange={(e) => onChange({ armour: { ...character.armour, name: e.target.value } })}
      />
    </label>
  );
}

/** "8 / 10 / 8 ..." for the caption: the armour now, location by location. */
export function armourSummary(target: Target, character: Character): string {
  const values = Object.values(target.armour ?? {});
  if (values.length === 0) return character.armour?.name || 'none';
  const name = character.armour?.name ? `${character.armour.name} · ` : '';
  return `${name}${Math.min(...values)}-${Math.max(...values)} AP`;
}

export default function ArmourBodySection(props: Props) {
  const { target } = props;
  // A humanoid mob has no locations, as hordes always have here. A mob of
  // VEHICLES keeps its facings: it is still made of vehicles (v8.44.0).
  const located = hasArmourLocations(resolveBodyShape(target), isCrowd(target));
  return (
    <>
      <ArmourName {...props} />
      <BodyChoice {...props} />
      {resolveBodyShape(target) === 'vehicle' && <KindChoice {...props} />}
      {resolveBodyShape(target) === 'object' && (
        <ObjectSection character={props.character} onChange={props.onChange} disabled={props.sheetLocked} />
      )}
      {located ? <Plates {...props} /> : <div className={styles.sectionNote}>A horde has no armour locations.</div>}
    </>
  );
}
